<template>
  <div class="messenger-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">

        <!-- No pages overlay -->
        <div v-if="hasPages === false" class="no-pages-overlay">
          <div class="no-pages-content">
            <img src="@/assets/messenger-logo.svg" alt="Messenger" class="no-pages-icon" />
            <h2>Messenger Not Connected</h2>
            <p>Connect a Facebook Page to start receiving and replying to Messenger conversations.</p>
            <router-link to="/settings/messenger" class="btn-setup">
              Connect Facebook Page
            </router-link>
          </div>
        </div>

        <div class="messenger-container" :class="{ 'split-view': selectedContact, 'blurred': hasPages === false }">

          <!-- Left Panel: Conversations -->
          <div class="conversations-panel" :class="{ 'mobile-hidden': selectedContact }">

            <div class="panel-header">
              <div class="panel-header-top">
                <button @click="loadInbox" class="btn-refresh" title="Refresh">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                </button>
              </div>

              <!-- Page filter -->
              <select v-if="pages.length > 1" v-model="selectedPageId" @change="loadInbox" class="page-filter">
                <option value="">All Pages</option>
                <option v-for="p in pages" :key="p.page_id" :value="p.page_id">{{ p.page_name }}</option>
              </select>

              <!-- Search -->
              <div class="search-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <input
                  v-model="searchQuery"
                  @input="onSearch"
                  type="text"
                  placeholder="Search contacts..."
                  class="search-input"
                />
              </div>

              <!-- Filter controls -->
              <div class="filter-controls">
                <button
                  @click="toggleUnread"
                  class="unread-toggle-btn"
                  :class="{ active: showUnreadOnly }"
                  :title="showUnreadOnly ? 'Show all conversations' : 'Show unread only'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span class="unread-text">Unread</span>
                </button>

                <button
                  @click="toggleArchived"
                  class="archive-toggle-btn"
                  :class="{ active: showArchived }"
                  :title="showArchived ? 'Show active conversations' : 'Show archived conversations'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.81.97H5.44l.8-.97zM5 19V8h14v11H5zm8.45-9L12 8.55 10.55 10H9.5v1h5v-1h-1.05z"/>
                  </svg>
                  <span class="archive-text">{{ showArchived ? 'Archived' : 'Archive' }}</span>
                </button>
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="loadingInbox" class="panel-loading">
              <div class="spinner"></div>
            </div>

            <!-- Empty state -->
            <div v-else-if="filteredContacts.length === 0" class="panel-empty">
              <svg width="40" height="40" viewBox="0 0 48 48" fill="none"><path fill="#9ca3af" d="M24 4C12.954 4 4 12.533 4 23.111c0 5.868 2.608 11.11 6.743 14.694V44l6.123-3.356A21.193 21.193 0 0024 41.222c11.046 0 20-8.534 20-18.111S35.046 4 24 4z"/></svg>
              <p>{{ showArchived ? 'No archived conversations' : showUnreadOnly ? 'No unread conversations' : 'No conversations yet' }}</p>
            </div>

            <!-- Contact list -->
            <div v-else class="contacts-list">
              <div
                v-for="contact in filteredContacts"
                :key="contact.psid + contact.page_id"
                class="contact-item"
                :class="{ active: selectedContact && selectedContact.psid === contact.psid && selectedContact.page_id === contact.page_id }"
                @click="selectContact(contact)"
              >
                <div class="contact-avatar">
                  <img v-if="contact.profile_pic" :src="contact.profile_pic" :alt="contact.name" class="avatar-img" @error="$event.target.style.display='none'" />
                  <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                </div>
                <div class="contact-info">
                  <div class="contact-name-row">
                    <span class="contact-name">{{ contact.name || contact.psid }}</span>
                    <span class="contact-time">{{ formatTime(contact.last_message_time || contact.last_message_at) }}</span>
                  </div>
                  <div class="contact-last-msg">
                    <span v-if="contact.last_role === 'Owner' || contact.last_role === 'Agent'" class="you-prefix">You: </span>
                    <span v-if="contact.last_attachment_type && !contact.last_message" class="attachment-preview">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/></svg>
                      {{ contact.last_attachment_type }}
                    </span>
                    <span v-else>{{ contact.last_message || 'No messages yet' }}</span>
                  </div>
                  <div v-if="pages.length > 1" class="contact-page">{{ contact.page_name }}</div>
                </div>
                <div class="contact-right-col">
                  <div v-if="contact.case_resolved == 0" class="unread-dot"></div>
                  <button
                    class="contact-archive-btn"
                    :title="showArchived ? 'Unarchive' : 'Archive'"
                    @click.stop="showArchived ? unarchiveContact(contact) : archiveContact(contact)"
                  >
                    <svg v-if="!showArchived" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.81.97H5.44l.8-.97zM5 19V8h14v11H5z"/>
                    </svg>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 3H3c-1.1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.72V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.72c.57-.38 1-1 1-1.72V5c0-1.1-.9-2-2-2zm-2 16H5V10h14v9zm2-11H3V5h18v3z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Panel: Chat -->
          <div class="chat-panel" v-if="selectedContact">
            <!-- Chat header -->
            <div class="chat-header">
              <button class="btn-back mobile-only" @click="selectedContact = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
              </button>
              <div class="chat-header-avatar">
                <img v-if="selectedContact.profile_pic" :src="selectedContact.profile_pic" :alt="selectedContact.name" class="avatar-img" @error="$event.target.style.display='none'" />
                <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              </div>
              <div class="chat-header-info">
                <div class="chat-header-name">{{ selectedContact.name || selectedContact.psid }}</div>
                <div class="chat-header-sub">{{ selectedContact.page_name || 'Messenger' }}</div>
              </div>
              <div class="chat-header-actions">
                <!-- Media Gallery button -->
                <button @click="toggleMediaGallery" class="media-gallery-btn" title="View Shared Media">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22,16V4A2,2 0 0,0 20,2H8A2,2 0 0,0 6,4V16A2,2 0 0,0 8,18H20A2,2 0 0,0 22,16M11,12L13.03,14.71L16,11L20,16H8M2,6V20A2,2 0 0,0 4,22H18V20H4V6"/></svg>
                  <span v-if="mediaGalleryTotals.total > 0" class="media-badge">{{ mediaGalleryTotals.total }}</span>
                </button>
              </div>

            </div>

            <!-- Messages -->
            <div class="chat-messages" ref="chatMessages">
              <div v-if="loadingMessages" class="messages-loading">
                <div class="spinner"></div>
              </div>
              <template v-else>
                <div
                  v-for="msg in messages"
                  :key="msg.id"
                  class="message-bubble-wrap"
                  :class="isOwnerMessage(msg) ? 'outgoing' : 'incoming'"
                  :data-mid="msg.messenger_message_id"
                >
                  <button v-if="!msg.sending" class="reply-btn" @click.stop="setReply(msg)" title="Reply">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                    </svg>
                  </button>
                  <div class="message-bubble" :class="{ 'bubble-sending': msg.sending }">
                    <!-- Reply quote -->
                    <div v-if="getReplySnapshot(msg)" class="msg-reply-quote" @click.stop="scrollToReplied(msg.reply_to_mid)">
                      <div class="reply-quote-sender">{{ getReplySnapshot(msg).sender }}</div>
                      <div class="reply-quote-body">
                        <img v-if="getReplySnapshot(msg).image_url" :src="getReplySnapshot(msg).image_url" class="reply-quote-thumb" @error="$event.target.style.display='none'" />
                        <span class="reply-quote-text">
                          <template v-if="getReplySnapshot(msg).attachment_type === 'image'">📷 Photo</template>
                          <template v-else-if="getReplySnapshot(msg).attachment_type === 'video'">🎥 Video</template>
                          <template v-else-if="getReplySnapshot(msg).attachment_type === 'audio'">🎵 Audio message</template>
                          <template v-else-if="getReplySnapshot(msg).attachment_type === 'file'">📎 Document</template>
                          <template v-else>{{ getReplySnapshot(msg).text }}</template>
                        </span>
                      </div>
                    </div>
                    <!-- Attachment -->
                    <template v-if="msg.attachment_type">
                      <img
                        v-if="(msg.attachment_type === 'image' || msg.attachment_type === 'sticker') && msg.image_url"
                        :src="msg.image_url"
                        class="msg-image msg-image-clickable"
                        @click="!msg.sending && openImageViewer(msg.image_url)"
                        @error="$event.target.style.display='none'"
                      />
                      <video v-else-if="msg.attachment_type === 'video' && msg.video_url" :src="msg.video_url" controls class="msg-video"></video>
                      <div v-else-if="msg.attachment_type === 'audio' && msg.audio_url" class="msg-audio-player">
                        <button @click="toggleAudio({ id: msg.id, url: msg.audio_url })" class="msg-audio-btn" :class="{ active: activeAudioItemId === msg.id && audioPlaying }">
                          <svg v-if="activeAudioItemId === msg.id && audioPlaying" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        </button>
                        <div class="msg-waveform" @click="seekAudio({ id: msg.id, url: msg.audio_url }, $event)">
                          <div class="msg-wavebars msg-wavebars-bg">
                            <span v-for="(h, i) in getWaveformBars(msg.id)" :key="i" class="msg-wavebar" :style="{ height: h + '%' }"></span>
                          </div>
                          <div class="msg-wavebars msg-wavebars-fg" :style="{ clipPath: 'inset(0 ' + (100 - (activeAudioItemId === msg.id ? audioProgress : 0)) + '% 0 0)' }">
                            <span v-for="(h, i) in getWaveformBars(msg.id)" :key="i" class="msg-wavebar" :style="{ height: h + '%' }"></span>
                          </div>
                        </div>
                        <span class="msg-audio-dur">{{ activeAudioItemId === msg.id && audioCurrentTime > 0 ? formatAudioTime(audioCurrentTime) : (activeAudioItemId === msg.id && audioDuration ? formatAudioTime(audioDuration) : '0:00') }}</span>
                      </div>
                      <a v-else-if="msg.document_url" :href="msg.document_url" target="_blank" rel="noopener" class="msg-doc">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                        {{ msg.message || 'Document' }}
                      </a>
                      <span v-else-if="msg.attachment_type === 'location'" class="msg-attachment-fallback">📍 {{ msg.message }}</span>
                      <span v-else class="msg-attachment-fallback">📎 {{ msg.attachment_type }}</span>
                    </template>
                    <!-- Text -->
                    <span v-if="msg.message && msg.attachment_type !== 'location' && msg.attachment_type !== 'file'" class="msg-text">{{ msg.message }}</span>
                    <span class="msg-time">
                      {{ formatMsgTime(msg.created_at || msg.date_time) }}
                      <!-- sending indicator -->
                      <span v-if="msg.sending" class="msg-sending-indicator">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" class="icon-spin"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 17.93V18a1 1 0 0 0-2 0v1.93A8 8 0 0 1 4.07 13H6a1 1 0 0 0 0-2H4.07A8 8 0 0 1 11 4.07V6a1 1 0 0 0 2 0V4.07A8 8 0 0 1 19.93 11H18a1 1 0 0 0 0 2h1.93A8 8 0 0 1 13 19.93z"/></svg>
                        Sending…
                      </span>
                    </span>
                  </div>
                </div>
                <div v-if="messages.length === 0" class="messages-empty">
                  <p>No messages yet</p>
                </div>
              </template>
            </div>

            <!-- Attachment preview (above input) -->
            <div v-if="selectedImage" class="attachment-preview-bar">
              <div v-if="selectedImage.compressing" class="preview-compressing">
                <div class="spinner-sm"></div>
                <span>Compressing…</span>
              </div>
              <template v-else>
                <img :src="selectedImage.preview" alt="Preview" class="preview-thumb" />
                <span class="preview-label">{{ selectedImage.name || 'Image' }}</span>
              </template>
              <button @click="removeSelectedImage" class="btn-remove-preview" title="Remove">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div v-if="selectedDocument" class="attachment-preview-bar doc-preview-bar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
              <span class="preview-label">{{ selectedDocument.name }}</span>
              <button @click="removeSelectedDocument" class="btn-remove-preview" title="Remove">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>

            <!-- Reply preview bar -->
            <div v-if="replyingTo" class="reply-preview-bar">
              <div class="reply-preview-line"></div>
              <div class="reply-preview-content">
                <span class="reply-preview-label">Replying to {{ isOwnerMessage(replyingTo) ? 'yourself' : (selectedContact.name || 'Customer') }}</span>
                <span class="reply-preview-text">
                  <template v-if="replyingTo.attachment_type === 'image'">📷 Photo</template>
                  <template v-else-if="replyingTo.attachment_type === 'video'">🎥 Video</template>
                  <template v-else-if="replyingTo.attachment_type === 'audio'">🎵 Audio message</template>
                  <template v-else-if="replyingTo.attachment_type === 'file'">📎 Document</template>
                  <template v-else>{{ replyingTo.message }}</template>
                </span>
              </div>
              <button @click="clearReply" class="btn-remove-preview" title="Cancel reply">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>

            <!-- Input area -->
            <div class="chat-input-area">

              <!-- Attachment button -->
              <div class="attachment-wrapper">
                <button
                  @click="toggleAttachmentMenu"
                  class="btn-icon-action"
                  :disabled="sending"
                  title="Attach file"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                  </svg>
                </button>
                <div v-if="showAttachmentMenu" class="attachment-menu">
                  <button @click="selectAttachmentType('image')" class="attachment-menu-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                    <span>Image</span>
                  </button>
                  <button @click="selectAttachmentType('document')" class="attachment-menu-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                    <span>Document</span>
                  </button>
                </div>
              </div>

              <!-- Hidden file inputs -->
              <input ref="imageInput" type="file" accept="image/*" @change="handleImageSelect" style="display:none" />
              <input ref="documentInput" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" @change="handleDocumentSelect" style="display:none" />

              <!-- Quick replies button -->
              <div class="quick-replies-wrapper">
                <button
                  @click="toggleQuickReplies"
                  class="btn-icon-action btn-qr"
                  :disabled="sending"
                  title="Quick Replies"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3C6.5 3 2 6.58 2 11c0 2.16.97 4.09 2.5 5.5V21l4.38-2.71C9.87 18.75 10.93 19 12 19c5.5 0 10-3.58 10-8s-4.5-8-10-8m0 14c-.67 0-1.34-.07-2-.21L8 18v-1.93c-1.65-1.31-2.73-3.28-2.73-5.57 0-3.31 3.13-6 7-6s7 2.69 7 6-3.13 6-7 6z"/>
                  </svg>
                  <span v-if="quickReplies.length > 0" class="qr-count">{{ quickReplies.length }}</span>
                </button>

                <!-- Quick Replies Dropdown -->
                <div v-if="showQuickRepliesDropdown" class="quick-replies-dropdown">
                  <div v-if="loadingQuickReplies" class="qr-loading">
                    <div class="spinner-sm"></div>
                    <span>Loading...</span>
                  </div>
                  <div v-else-if="quickReplies.length === 0" class="qr-empty">
                    <p>No quick replies yet</p>
                    <router-link to="/quick-replies" class="qr-create-link">Create Quick Replies</router-link>
                  </div>
                  <div v-else class="qr-list">
                    <div
                      v-for="reply in quickReplies"
                      :key="reply.id"
                      @click="useQuickReply(reply)"
                      class="qr-item"
                    >
                      <img v-if="reply.image_url" :src="reply.image_url" class="qr-thumb" alt="" />
                      <div class="qr-text-wrap">
                        <div class="qr-title">{{ reply.title }}</div>
                        <div class="qr-preview">
                          <span v-if="reply.message">{{ reply.message.substring(0, 60) }}{{ reply.message.length > 60 ? '...' : '' }}</span>
                          <span v-else class="qr-img-only">📷 Image only</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Textarea -->
              <textarea
                v-model="newMessage"
                @keydown.enter.exact.prevent="sendMessage"
                placeholder="Type a message... (Enter to send)"
                class="chat-input"
                rows="1"
                :disabled="sending"
              ></textarea>

              <!-- Send button -->
              <button
                @click="sendMessage"
                class="btn-send"
                :disabled="(!newMessage.trim() && !selectedImage && !selectedDocument) || sending"
              >
                <svg v-if="!sending" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
                <div v-else class="spinner-sm"></div>
              </button>
            </div>
          </div>

          <!-- No conversation selected -->
          <div v-else class="chat-empty-state">
            <img src="@/assets/messenger-logo.svg" width="64" height="64" alt="" class="empty-logo" />
            <h3>Select a conversation</h3>
            <p>Choose a contact from the left to start chatting.</p>
          </div>

        </div>
      </main>
    </div>

    <!-- Media Gallery — teleported to body so position:fixed works reliably -->
    <Teleport to="body">
      <div v-if="showMediaGallery" class="media-gallery-overlay" @click="closeMediaGallery">
        <div class="media-gallery-panel" @click.stop>
          <div class="media-gallery-header">
            <h3>Shared Media</h3>
            <button @click="closeMediaGallery" class="close-gallery-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>

          <div class="media-tabs">
            <button @click="activeMediaTab = 'images'" class="media-tab" :class="{ active: activeMediaTab === 'images' }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/></svg>
              <span>Images ({{ mediaGalleryTotals.images }})</span>
            </button>
            <button @click="activeMediaTab = 'videos'" class="media-tab" :class="{ active: activeMediaTab === 'videos' }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/></svg>
              <span>Videos ({{ mediaGalleryTotals.videos }})</span>
            </button>
            <button @click="activeMediaTab = 'audio'" class="media-tab" :class="{ active: activeMediaTab === 'audio' }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/></svg>
              <span>Audio ({{ mediaGalleryTotals.audio }})</span>
            </button>
            <button @click="activeMediaTab = 'documents'" class="media-tab" :class="{ active: activeMediaTab === 'documents' }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20M10 19L12 15H9V10H13L11 14H14V19H10Z"/></svg>
              <span>Docs ({{ mediaGalleryTotals.documents }})</span>
            </button>
          </div>

          <div v-if="loadingMedia" class="media-gallery-loading">
            <div class="spinner"></div>
            <p>Loading media…</p>
          </div>

          <div v-else class="media-gallery-content">
            <!-- Images -->
            <div v-if="activeMediaTab === 'images'" class="media-grid">
              <div v-if="mediaGalleryData.images.length === 0" class="no-media">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3"><path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/></svg>
                <p>No images shared</p>
              </div>
              <div v-for="item in mediaGalleryData.images" :key="item.id" class="media-item" @click="openImageViewer(item.url)">
                <img :src="item.url" alt="Shared image" @error="$event.target.style.display='none'" />
                <div class="media-item-overlay">
                  <button @click.stop="downloadMedia(item.url)" class="media-download-btn" title="Download">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/></svg>
                  </button>
                </div>
                <div class="media-item-date">{{ formatMediaDate(item.created_at) }}</div>
              </div>
            </div>

            <!-- Videos -->
            <div v-if="activeMediaTab === 'videos'" class="media-grid">
              <div v-if="mediaGalleryData.videos.length === 0" class="no-media">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3"><path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/></svg>
                <p>No videos shared</p>
              </div>
              <div v-for="item in mediaGalleryData.videos" :key="item.id" class="media-item video-item">
                <video :src="item.url" class="media-video-preview"></video>
                <div class="media-item-overlay">
                  <button @click="playVideo(item.url)" class="media-play-btn" title="Play">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8,5.14V19.14L19,12.14L8,5.14Z"/></svg>
                  </button>
                  <button @click.stop="downloadMedia(item.url)" class="media-download-btn" title="Download">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/></svg>
                  </button>
                </div>
                <div class="media-item-date">{{ formatMediaDate(item.created_at) }}</div>
              </div>
            </div>

            <!-- Audio -->
            <div v-if="activeMediaTab === 'audio'" class="media-list">
              <div v-if="mediaGalleryData.audio.length === 0" class="no-media">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3"><path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/></svg>
                <p>No audio shared</p>
              </div>
              <div v-for="item in mediaGalleryData.audio" :key="item.id" class="media-audio-item">
                <button
                  @click="toggleAudio(item)"
                  class="audio-play-btn"
                  :class="{ playing: activeAudioItemId === item.id && audioPlaying }"
                >
                  <svg v-if="activeAudioItemId === item.id && audioPlaying" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </button>
                <div class="audio-body">
                  <div class="audio-waveform" @click="seekAudio(item, $event)">
                    <div
                      class="audio-waveform-fill"
                      :style="{ width: (activeAudioItemId === item.id ? audioProgress : 0) + '%' }"
                    ></div>
                  </div>
                  <div class="audio-meta">
                    <span class="audio-time-current">{{ activeAudioItemId === item.id ? formatAudioTime(audioCurrentTime) : '0:00' }}</span>
                    <span class="audio-date-label">{{ formatMediaDate(item.created_at) }}</span>
                    <span class="audio-time-total">{{ activeAudioItemId === item.id && audioDuration ? formatAudioTime(audioDuration) : '' }}</span>
                  </div>
                </div>
                <button @click="downloadMedia(item.url)" class="media-download-btn-small" title="Download">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/></svg>
                </button>
              </div>
            </div>

            <!-- Documents -->
            <div v-if="activeMediaTab === 'documents'" class="media-list">
              <div v-if="mediaGalleryData.documents.length === 0" class="no-media">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3"><path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/></svg>
                <p>No documents shared</p>
              </div>
              <div v-for="item in mediaGalleryData.documents" :key="item.id" class="media-document-item">
                <div class="document-info">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/></svg>
                  <div class="document-details">
                    <span class="document-name">{{ getDocumentName(item.url) }}</span>
                    <span class="document-date">{{ formatMediaDate(item.created_at) }}</span>
                  </div>
                </div>
                <div class="document-actions">
                  <button @click="openDocument(item.url)" class="document-view-btn" title="Open">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/></svg>
                  </button>
                  <button @click="downloadMedia(item.url)" class="media-download-btn-small" title="Download">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Image viewer lightbox -->
    <div v-if="showImageViewer" class="image-viewer-overlay" @click="closeImageViewer">
      <div class="image-viewer-container" @click.stop>
        <div class="image-viewer-actions">
          <a :href="viewerImageUrl" target="_blank" rel="noopener" class="btn-viewer-download" title="Open in new tab">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59L7.76 14.83l1.41 1.41L19 5.41V9h2V3h-7z"/></svg>
          </a>
          <button @click="closeImageViewer" class="btn-viewer-close" title="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        <img :src="viewerImageUrl" alt="Full size" class="viewer-img" />
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'
import { AuthService } from '@/services/auth'
import { io } from 'socket.io-client'

export default {
  name: 'MessengerView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      hasPages: null,
      pages: [],
      selectedPageId: '',
      contacts: [],
      loadingInbox: true,
      searchQuery: '',
      searchTimer: null,
      showUnreadOnly: false,
      showArchived: false,
      selectedContact: null,
      messages: [],
      loadingMessages: false,
      newMessage: '',
      sending: false,
      replyingTo: null,
      socket: null,
      // Attachments
      selectedImage: null,
      selectedDocument: null,
      showAttachmentMenu: false,
      // Quick replies
      quickReplies: [],
      loadingQuickReplies: false,
      showQuickRepliesDropdown: false,
      // Image viewer
      showImageViewer: false,
      viewerImageUrl: null,
      // Timezone
      userTimezone: null,
      // Media gallery
      showMediaGallery: false,
      loadingMedia: false,
      activeMediaTab: 'images',
      mediaGalleryData: {
        images: [],
        videos: [],
        audio: [],
        documents: []
      },
      mediaGalleryTotals: {
        images: 0,
        videos: 0,
        audio: 0,
        documents: 0,
        total: 0
      },
      // Custom audio player
      activeAudioItemId: null,
      audioProgress: 0,
      audioCurrentTime: 0,
      audioDuration: 0,
      audioPlaying: false,
      audioEl: null
    }
  },

  mounted () {
    this.init()
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeyDown)
  },

  beforeUnmount () {
    this.stopAudio()
    if (this.socket) this.socket.disconnect()
    clearTimeout(this.searchTimer)
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeyDown)
  },

  computed: {
    filteredContacts () {
      if (this.showUnreadOnly) return this.contacts.filter(c => c.case_resolved === 0 || c.case_resolved === '0')
      return this.contacts
    }
  },

  methods: {
    async init () {
      // Resolve user timezone from country_code
      const userData = AuthService.getUserData()
      this.userTimezone = this.countryCodeToTimezone(userData?.country_code)

      const resp = await DashboardService.getMessengerPages()
      this.pages = resp.pages?.filter(p => p.active === 1) || []
      this.hasPages = this.pages.length > 0
      if (this.hasPages) {
        await Promise.all([this.loadInbox(), this.loadQuickReplies()])
        this.connectSocket()
      } else {
        this.loadingInbox = false
      }
    },

    async loadInbox () {
      this.loadingInbox = true
      try {
        const resp = await DashboardService.getMessengerInbox({
          pageId: this.selectedPageId,
          search: this.searchQuery,
          archived: this.showArchived ? 1 : 0
        })
        this.contacts = resp.contacts || []
      } finally {
        this.loadingInbox = false
      }
    },

    toggleUnread () {
      this.showUnreadOnly = !this.showUnreadOnly
    },

    toggleArchived () {
      this.showArchived = !this.showArchived
      this.showUnreadOnly = false
      this.loadInbox()
    },

    async archiveContact (contact) {
      await DashboardService.archiveMessengerContact(contact.psid, contact.page_id)
      this.contacts = this.contacts.filter(c => !(c.psid === contact.psid && c.page_id === contact.page_id))
      if (this.selectedContact?.psid === contact.psid) this.selectedContact = null
    },

    async unarchiveContact (contact) {
      await DashboardService.unarchiveMessengerContact(contact.psid, contact.page_id)
      this.contacts = this.contacts.filter(c => !(c.psid === contact.psid && c.page_id === contact.page_id))
      if (this.selectedContact?.psid === contact.psid) this.selectedContact = null
    },

    onSearch () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadInbox(), 350)
    },

    async selectContact (contact) {
      this.selectedContact = contact
      this.messages = []
      this.loadingMessages = true
      // Reset gallery state for new contact
      this.showMediaGallery = false
      this.mediaGalleryData = { images: [], videos: [], audio: [], documents: [] }
      this.mediaGalleryTotals = { images: 0, videos: 0, audio: 0, documents: 0, total: 0 }
      try {
        const resp = await DashboardService.getMessengerConversation(contact.psid, contact.page_id)
        this.messages = resp.messages || []
      } finally {
        this.loadingMessages = false
        this.scrollToBottomAsync()
      }
    },

    // ── Quick Replies ──────────────────────────────────────────────────────

    async loadQuickReplies () {
      this.loadingQuickReplies = true
      try {
        const result = await DashboardService.getQuickReplies()
        this.quickReplies = result.success ? (result.quick_replies || []) : []
      } catch (_) {
        this.quickReplies = []
      } finally {
        this.loadingQuickReplies = false
      }
    },

    toggleQuickReplies () {
      this.showQuickRepliesDropdown = !this.showQuickRepliesDropdown
      this.showAttachmentMenu = false
    },

    useQuickReply (reply) {
      this.newMessage = reply.message || ''
      if (reply.image_url) {
        this.selectedImage = { preview: reply.image_url, file: null, name: reply.title || 'Image', fromQuickReply: true }
      } else {
        this.selectedImage = null
      }
      this.selectedDocument = null
      this.showQuickRepliesDropdown = false
    },

    // ── Attachments ────────────────────────────────────────────────────────

    toggleAttachmentMenu () {
      this.showAttachmentMenu = !this.showAttachmentMenu
      this.showQuickRepliesDropdown = false
    },

    selectAttachmentType (type) {
      this.showAttachmentMenu = false
      if (type === 'image') {
        this.$refs.imageInput.value = ''
        this.$refs.imageInput.click()
      } else {
        this.$refs.documentInput.value = ''
        this.$refs.documentInput.click()
      }
    },

    async handleImageSelect (event) {
      const file = event.target.files[0]
      if (!file) return

      // Show placeholder while compressing
      const originalPreview = URL.createObjectURL(file)
      this.selectedImage = {
        file: null,
        preview: originalPreview,
        name: file.name,
        fromQuickReply: false,
        compressing: true
      }
      this.selectedDocument = null

      try {
        const compressed = await this.compressImage(file)
        const newPreview = URL.createObjectURL(compressed)
        URL.revokeObjectURL(originalPreview)
        this.selectedImage = {
          file: compressed,
          preview: newPreview,
          name: file.name,
          fromQuickReply: false,
          compressing: false
        }
      } catch {
        // Compression failed — fall back to original
        this.selectedImage = {
          file,
          preview: originalPreview,
          name: file.name,
          fromQuickReply: false,
          compressing: false
        }
      }
    },

    handleDocumentSelect (event) {
      const file = event.target.files[0]
      if (!file) return
      const MAX_DOC = 25 * 1024 * 1024 // 25 MB
      if (file.size > MAX_DOC) {
        alert(`File too large (${this.formatBytes(file.size)}). Maximum allowed size is 25 MB.`)
        event.target.value = ''
        return
      }
      this.selectedDocument = file
      this.selectedImage = null
    },

    compressImage (file) {
      return new Promise((resolve, reject) => {
        const MAX_SIDE = 1280
        const QUALITY = 0.85
        // Keep original MIME type so the server extension check still passes
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'

        const img = new Image()
        const objectUrl = URL.createObjectURL(file)

        img.onload = () => {
          URL.revokeObjectURL(objectUrl)

          let { width, height } = img
          if (width > MAX_SIDE || height > MAX_SIDE) {
            if (width >= height) {
              height = Math.round(height * (MAX_SIDE / width))
              width = MAX_SIDE
            } else {
              width = Math.round(width * (MAX_SIDE / height))
              height = MAX_SIDE
            }
          }

          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          canvas.getContext('2d').drawImage(img, 0, 0, width, height)

          canvas.toBlob(blob => {
            if (blob && blob.size < file.size) {
              // Wrap in File to preserve the original filename for the server
              resolve(new File([blob], file.name, { type: mimeType }))
            } else {
              // Already small — use original as-is
              resolve(file)
            }
          }, mimeType, QUALITY)
        }

        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')) }
        img.src = objectUrl
      })
    },

    formatBytes (bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    removeSelectedImage () {
      if (this.selectedImage && !this.selectedImage.fromQuickReply && this.selectedImage.preview) {
        URL.revokeObjectURL(this.selectedImage.preview)
      }
      this.selectedImage = null
      if (this.$refs.imageInput) this.$refs.imageInput.value = ''
    },

    removeSelectedDocument () {
      this.selectedDocument = null
      if (this.$refs.documentInput) this.$refs.documentInput.value = ''
    },

    // ── Reply helpers ──────────────────────────────────────────────────────

    setReply (msg) {
      this.replyingTo = msg
      this.$nextTick(() => {
        const input = this.$el.querySelector('.chat-input')
        if (input) input.focus()
      })
    },

    clearReply () {
      this.replyingTo = null
    },

    getReplySnapshot (msg) {
      if (msg.reply_to_snapshot) {
        try {
          return typeof msg.reply_to_snapshot === 'string'
            ? JSON.parse(msg.reply_to_snapshot)
            : msg.reply_to_snapshot
        } catch (_) {}
      }
      if (msg.reply_to_mid) {
        const orig = this.messages.find(m => m.messenger_message_id === msg.reply_to_mid)
        if (orig) {
          return {
            sender: this.isOwnerMessage(orig)
              ? 'You'
              : (this.selectedContact?.name || 'Customer'),
            text: orig.message || null,
            image_url: orig.image_url || null,
            attachment_type: orig.attachment_type || null
          }
        }
        return { sender: '', text: 'Replied to a message', image_url: null, attachment_type: null }
      }
      return null
    },

    scrollToReplied (mid) {
      if (!mid) return
      const el = this.$el.querySelector(`[data-mid="${mid}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    // ── Send message ───────────────────────────────────────────────────────

    async sendMessage () {
      const text = this.newMessage.trim()
      const image = this.selectedImage
      const doc = this.selectedDocument

      if (!text && !image && !doc) return
      if (this.sending) return

      this.sending = true
      const pageId = this.selectedContact.page_id
      const psid = this.selectedContact.psid

      // Capture reply context and clear it immediately
      const replyMid = this.replyingTo?.messenger_message_id || null
      const replySnapshot = replyMid
        ? {
            sender: this.isOwnerMessage(this.replyingTo)
              ? 'You'
              : (this.selectedContact?.name || 'Customer'),
            text: this.replyingTo.message || null,
            image_url: this.replyingTo.image_url || null,
            attachment_type: this.replyingTo.attachment_type || null
          }
        : null
      this.clearReply()

      // Snapshot and clear inputs immediately
      this.newMessage = ''
      this.selectedImage = null
      this.selectedDocument = null

      try {
        // Send image
        if (image) {
          const tempId = 'tmp_' + Date.now()
          this.messages.push({
            id: tempId,
            from: pageId,
            to: psid,
            message: null,
            attachment_type: 'image',
            image_url: image.preview,
            created_at: new Date().toLocaleString('sv'),
            conversation_role: 'Owner',
            sending: true,
            reply_to_snapshot: replySnapshot
          })
          this.scrollToBottomAsync()

          let imageUrl
          if (image.fromQuickReply) {
            imageUrl = image.preview
          } else {
            const up = await DashboardService.uploadMessengerAttachment(image.file)
            if (!up.success) throw new Error(up.message || 'Upload failed')
            imageUrl = up.url
          }
          const imgMsgObj = { attachment: { type: 'image', payload: { url: imageUrl, is_reusable: true } } }
          const imgResp = await DashboardService.sendMessengerMessage({ pageId, psid, messageObject: imgMsgObj, replyToMid: replyMid, replyToSnapshot: replySnapshot })
          const bubble = this.messages.find(m => m.id === tempId)
          if (bubble) {
            bubble.image_url = imageUrl
            bubble.sending = false
            if (imgResp.message_id) bubble.messenger_message_id = imgResp.message_id
          }
          if (!image.fromQuickReply) {
            await this.$nextTick()
            URL.revokeObjectURL(image.preview)
          }
        }

        // Send document
        if (doc) {
          const tempId = 'tmp_' + Date.now()
          this.messages.push({
            id: tempId,
            from: pageId,
            to: psid,
            message: doc.name,
            attachment_type: 'file',
            document_url: null,
            created_at: new Date().toLocaleString('sv'),
            conversation_role: 'Owner',
            sending: true,
            reply_to_snapshot: replySnapshot
          })
          this.scrollToBottomAsync()

          const up = await DashboardService.uploadMessengerAttachment(doc)
          if (up.success) {
            const docMsgObj = { attachment: { type: 'file', payload: { url: up.url, is_reusable: true } } }
            const docResp = await DashboardService.sendMessengerMessage({ pageId, psid, messageObject: docMsgObj, replyToMid: replyMid, replyToSnapshot: replySnapshot })
            const bubble = this.messages.find(m => m.id === tempId)
            if (bubble) {
              bubble.document_url = up.url
              bubble.sending = false
              if (docResp.message_id) bubble.messenger_message_id = docResp.message_id
            }
          }
        }

        // Send text
        if (text) {
          const tempId = 'tmp_' + Date.now()
          this.messages.push({
            id: tempId,
            from: pageId,
            to: psid,
            message: text,
            created_at: new Date().toLocaleString('sv'),
            conversation_role: 'Owner',
            sending: true,
            reply_to_snapshot: replySnapshot
          })
          this.scrollToBottomAsync()

          const resp = await DashboardService.sendMessengerMessage({ pageId, psid, messageObject: { text }, replyToMid: replyMid, replyToSnapshot: replySnapshot })
          const bubble = this.messages.find(m => m.id === tempId)
          if (bubble) {
            bubble.sending = !resp.success
            if (resp.message_id) bubble.messenger_message_id = resp.message_id
          }
        }
      } catch (e) {
        console.error('Send error:', e)
        // Restore text on error
        if (!image && !doc) this.newMessage = text
      } finally {
        this.sending = false
      }
    },

    // ── Image viewer ───────────────────────────────────────────────────────

    openImageViewer (url) {
      if (!url) return
      this.viewerImageUrl = url
      this.showImageViewer = true
      document.body.style.overflow = 'hidden'
    },

    closeImageViewer () {
      this.showImageViewer = false
      this.viewerImageUrl = null
      document.body.style.overflow = ''
    },

    // ── Polling ────────────────────────────────────────────────────────────

    connectSocket () {
      const token = AuthService.getAuthToken()
      if (!token) return

      this.socket = io('https://messenger.quali-d.com', {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 10,
        reconnectionDelay: 2000
      })

      this.socket.on('connect', () => {
        console.log('[Messenger] Socket connected:', this.socket.id)
      })

      this.socket.on('connect_error', (err) => {
        console.warn('[Messenger] Socket connect error:', err.message)
      })

      this.socket.on('new_message', (msg) => {
        // Play sound for incoming customer messages
        if (msg.conversation_role === 'Customer') {
          this.playNotificationSound()
        }

        // Update inbox contact preview
        const contact = this.contacts.find(
          c => c.psid === msg.psid && c.page_id === msg.page_id
        )
        if (contact) {
          contact.last_message = msg.message || `[${msg.attachment_type || 'attachment'}]`
          contact.last_message_time = msg.created_at
          contact.last_role = msg.conversation_role
          // Bubble to top of list
          const idx = this.contacts.indexOf(contact)
          if (idx > 0) {
            this.contacts.splice(idx, 1)
            this.contacts.unshift(contact)
          }
        }

        // Push into open conversation if it matches
        if (
          this.selectedContact &&
          msg.psid === this.selectedContact.psid &&
          msg.page_id === this.selectedContact.page_id
        ) {
          // Deduplicate by messenger_message_id
          const isDuplicate = msg.messenger_message_id &&
            this.messages.some(m => m.messenger_message_id === msg.messenger_message_id)
          if (!isDuplicate) {
            this.messages.push(msg)
            this.scrollToBottomAsync()
          }
        }
      })
    },

    // ── Utilities ──────────────────────────────────────────────────────────

    playNotificationSound () {
      if (!this._notifAudio) {
        this._notifAudio = new Audio('/messenger.mp3')
        this._notifAudio.volume = 0.6
      }
      this._notifAudio.currentTime = 0
      this._notifAudio.play().catch(() => {})
    },

    scrollToBottom () {
      const el = this.$refs.chatMessages
      if (el) el.scrollTop = el.scrollHeight
    },

    scrollToBottomAsync () {
      this.$nextTick(() => {
        this.scrollToBottom()
        // Second pass catches images / dynamic content that shifts height
        setTimeout(() => this.scrollToBottom(), 150)
      })
    },

    isOwnerMessage (msg) {
      return msg.conversation_role === 'Owner' || msg.conversation_role === 'Agent' || msg.conversation_role === 'Bot'
    },

    handleClickOutside (event) {
      const attachWrap = this.$el?.querySelector('.attachment-wrapper')
      if (attachWrap && !attachWrap.contains(event.target)) {
        this.showAttachmentMenu = false
      }
      const qrWrap = this.$el?.querySelector('.quick-replies-wrapper')
      if (qrWrap && !qrWrap.contains(event.target)) {
        this.showQuickRepliesDropdown = false
      }
    },

    handleKeyDown (e) {
      if (e.key === 'Escape') {
        if (this.showImageViewer) this.closeImageViewer()
        if (this.showMediaGallery) this.closeMediaGallery()
        this.showAttachmentMenu = false
        this.showQuickRepliesDropdown = false
      }
    },

    formatTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const now = new Date()
      const diff = now - d
      if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' })
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },

    formatMsgTime (dateStr) {
      if (!dateStr) return ''
      // created_at is server local time (same tz as user) — parse as-is, no Z suffix
      // ISO strings from optimistic messages have T+Z — strip the Z so they're also local
      const normalized = String(dateStr).replace('T', ' ').replace('Z', '').substring(0, 19)
      const d = new Date(normalized)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: this.userTimezone || undefined
      })
    },

    countryCodeToTimezone (dialCode) {
      // Map phone dial codes → IANA timezone
      // For ambiguous codes (+1) we pick the most common zone; user's browser locale
      // is the fallback anyway.
      const map = {
        1: 'America/New_York',
        7: 'Europe/Moscow',
        20: 'Africa/Cairo',
        27: 'Africa/Johannesburg',
        30: 'Europe/Athens',
        31: 'Europe/Amsterdam',
        32: 'Europe/Brussels',
        33: 'Europe/Paris',
        34: 'Europe/Madrid',
        36: 'Europe/Budapest',
        39: 'Europe/Rome',
        40: 'Europe/Bucharest',
        41: 'Europe/Zurich',
        43: 'Europe/Vienna',
        44: 'Europe/London',
        45: 'Europe/Copenhagen',
        46: 'Europe/Stockholm',
        47: 'Europe/Oslo',
        48: 'Europe/Warsaw',
        49: 'Europe/Berlin',
        51: 'America/Lima',
        52: 'America/Mexico_City',
        54: 'America/Argentina/Buenos_Aires',
        55: 'America/Sao_Paulo',
        56: 'America/Santiago',
        57: 'America/Bogota',
        58: 'America/Caracas',
        60: 'Asia/Kuala_Lumpur',
        61: 'Australia/Sydney',
        62: 'Asia/Jakarta',
        63: 'Asia/Manila',
        64: 'Pacific/Auckland',
        65: 'Asia/Singapore',
        66: 'Asia/Bangkok',
        81: 'Asia/Tokyo',
        82: 'Asia/Seoul',
        84: 'Asia/Ho_Chi_Minh',
        86: 'Asia/Shanghai',
        90: 'Europe/Istanbul',
        91: 'Asia/Kolkata',
        92: 'Asia/Karachi',
        93: 'Asia/Kabul',
        94: 'Asia/Colombo',
        95: 'Asia/Rangoon',
        98: 'Asia/Tehran',
        212: 'Africa/Casablanca',
        213: 'Africa/Algiers',
        216: 'Africa/Tunis',
        218: 'Africa/Tripoli',
        220: 'Africa/Banjul',
        234: 'Africa/Lagos',
        249: 'Africa/Khartoum',
        251: 'Africa/Addis_Ababa',
        254: 'Africa/Nairobi',
        256: 'Africa/Kampala',
        966: 'Asia/Riyadh',
        967: 'Asia/Aden',
        968: 'Asia/Muscat',
        970: 'Asia/Gaza',
        971: 'Asia/Dubai',
        972: 'Asia/Jerusalem',
        973: 'Asia/Bahrain',
        974: 'Asia/Qatar',
        975: 'Asia/Thimphu',
        976: 'Asia/Ulaanbaatar',
        977: 'Asia/Kathmandu',
        994: 'Asia/Baku',
        998: 'Asia/Tashkent'
      }
      const tz = dialCode ? map[String(dialCode).trim()] : null
      // Validate the timezone exists in this browser before using it
      if (tz) {
        try {
          Intl.DateTimeFormat(undefined, { timeZone: tz })
          return tz
        } catch { /* fall through */ }
      }
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    },

    // ── Media Gallery ──────────────────────────────────────────────────────

    toggleMediaGallery () {
      if (this.showMediaGallery) {
        this.closeMediaGallery()
      } else {
        this.showMediaGallery = true
        this.loadConversationMedia()
      }
    },

    closeMediaGallery () {
      this.showMediaGallery = false
      this.stopAudio()
    },

    async loadConversationMedia () {
      if (!this.selectedContact) return
      this.loadingMedia = true
      try {
        const resp = await DashboardService.getMessengerMedia(
          this.selectedContact.psid,
          this.selectedContact.page_id
        )
        if (resp.success) {
          this.mediaGalleryData = {
            images: resp.images || [],
            videos: resp.videos || [],
            audio: resp.audio || [],
            documents: resp.documents || []
          }
          this.mediaGalleryTotals = resp.totals || {
            images: 0,
            videos: 0,
            audio: 0,
            documents: 0,
            total: 0
          }
        }
      } finally {
        this.loadingMedia = false
      }
    },

    formatMediaDate (dateStr) {
      if (!dateStr) return ''
      const normalized = String(dateStr).replace('T', ' ').replace('Z', '').substring(0, 19)
      const d = new Date(normalized)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    },

    downloadMedia (url) {
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.download = url.split('/').pop().split('?')[0] || 'download'
      a.target = '_blank'
      a.rel = 'noopener'
      a.click()
    },

    playVideo (url) {
      this.openImageViewer(url)
    },

    openDocument (url) {
      if (!url) return
      window.open(url, '_blank', 'noopener')
    },

    getDocumentName (url) {
      if (!url) return 'Document'
      try {
        const name = new URL(url).pathname.split('/').pop()
        return decodeURIComponent(name) || 'Document'
      } catch {
        return url.split('/').pop().split('?')[0] || 'Document'
      }
    },

    // ── Custom Audio Player ────────────────────────────────────────────────

    getWaveformBars (itemId) {
      if (!this._waveformCache) this._waveformCache = {}
      const key = String(itemId)
      if (this._waveformCache[key]) return this._waveformCache[key]
      // Deterministic pseudo-random heights seeded by item id
      let seed = 0
      for (let i = 0; i < key.length; i++) {
        seed = Math.imul(31, seed) + key.charCodeAt(i) | 0
      }
      const bars = []
      for (let i = 0; i < 30; i++) {
        seed = Math.imul(1664525, seed) + 1013904223 | 0
        bars.push(20 + (Math.abs(seed) % 80))
      }
      this._waveformCache[key] = bars
      return bars
    },

    toggleAudio (item) {
      if (this.audioEl && this.audioEl._itemId === item.id) {
        if (this.audioPlaying) {
          this.audioEl.pause()
          this.audioPlaying = false
        } else {
          this.audioEl.play()
          this.audioPlaying = true
        }
        return
      }
      this.stopAudio()
      const el = new Audio(item.url)
      el._itemId = item.id
      el.addEventListener('timeupdate', () => {
        this.audioProgress = el.duration ? (el.currentTime / el.duration) * 100 : 0
        this.audioCurrentTime = el.currentTime
        this.audioDuration = el.duration || 0
      })
      el.addEventListener('loadedmetadata', () => {
        this.audioDuration = el.duration || 0
      })
      el.addEventListener('ended', () => {
        this.audioPlaying = false
        this.audioProgress = 0
        this.audioCurrentTime = 0
      })
      this.audioEl = el
      this.activeAudioItemId = item.id
      el.play().then(() => { this.audioPlaying = true }).catch(() => {})
    },

    stopAudio () {
      if (this.audioEl) {
        this.audioEl.pause()
        this.audioEl = null
      }
      this.activeAudioItemId = null
      this.audioProgress = 0
      this.audioCurrentTime = 0
      this.audioDuration = 0
      this.audioPlaying = false
    },

    seekAudio (item, event) {
      if (!this.audioEl || this.audioEl._itemId !== item.id) {
        this.toggleAudio(item)
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      this.audioEl.currentTime = pct * (this.audioEl.duration || 0)
    },

    formatAudioTime (seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.messenger-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: var(--bg-primary, #111827);
  color: var(--text-primary, #f9fafb);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
  overflow: hidden;
}

.page-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* No Pages Overlay */
.no-pages-overlay {
  position: absolute;
  inset: 0;
  background: var(--bg-primary, #111827);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.no-pages-content {
  text-align: center;
  padding: 40px 24px;
}

.no-pages-icon {
  width: 72px;
  height: 72px;
  margin-bottom: 20px;
  opacity: 0.7;
}

.no-pages-content h2 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 10px;
}

.no-pages-content p {
  color: var(--text-secondary, #9ca3af);
  font-size: 14px;
  margin: 0 0 20px;
}

.btn-setup {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #8A3FFC, #006AFF);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s;
}

.btn-setup:hover { opacity: 0.9; }

.blurred { filter: blur(3px); pointer-events: none; user-select: none; }

/* Messenger Container */
.messenger-container {
  display: flex;
  flex: 1;
  overflow: hidden;
  height: calc(100vh - 64px);
}

/* Left Panel */
.conversations-panel {
  width: 340px;
  min-width: 280px;
  max-width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-primary);
  background: var(--bg-card);
  overflow: hidden;
}

.panel-header {
  padding: 16px 14px 12px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.panel-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary, #f9fafb);
}

.btn-refresh {
  background: none;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}

.btn-refresh:hover { color: var(--text-primary, #f9fafb); }

.page-filter {
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-primary);
  color: var(--text-primary, #f9fafb);
  border-radius: 7px;
  padding: 6px 10px;
  font-size: 12px;
  width: 100%;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: var(--text-secondary, #6b7280);
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-primary);
  color: var(--text-primary, #f9fafb);
  border-radius: 8px;
  padding: 7px 10px 7px 32px;
  font-size: 13px;
  outline: none;
}

.search-input:focus { border-color: #006AFF; }

/* Filter controls */
.filter-controls {
  display: flex;
  gap: 8px;
}

.archive-toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  justify-content: center;
}

.archive-toggle-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.archive-toggle-btn.active {
  background: #25D366;
  border-color: #25D366;
  color: white;
}

.archive-toggle-btn.active:hover {
  background: #22c55e;
  border-color: #22c55e;
}

.archive-text { font-weight: 500; }

.unread-toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  justify-content: center;
}

.unread-toggle-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.unread-toggle-btn.active {
  background: #3B82F6;
  border-color: #3B82F6;
  color: white;
}

.unread-toggle-btn.active:hover {
  background: #2563EB;
  border-color: #2563EB;
}

.unread-text { font-weight: 500; }

/* Loading / empty states */
.panel-loading, .panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

.contacts-list {
  flex: 1;
  overflow-y: auto;
}

/* Contact item */
.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(55, 65, 81, 0.4);
  transition: background 0.1s;
  position: relative;
}

.contact-item:hover { background: rgba(0, 106, 255, 0.05); }
.contact-item.active { background: rgba(0, 106, 255, 0.1); }

.contact-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(138, 63, 252, 0.15);
  color: #8A3FFC;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.avatar-img {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 50%;
}

.contact-info { flex: 1; min-width: 0; }

.contact-name-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}

.contact-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary, #f9fafb);
}

.contact-time {
  font-size: 10px;
  color: var(--text-secondary, #6b7280);
  flex-shrink: 0;
}

.contact-last-msg {
  font-size: 12px;
  color: var(--text-secondary, #9ca3af);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.you-prefix { color: var(--text-secondary, #6b7280); }
.attachment-preview { display: inline-flex; align-items: center; gap: 3px; }

.contact-page {
  font-size: 10px;
  color: #006AFF;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-right-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #006AFF;
  flex-shrink: 0;
}

.contact-archive-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;
}

.contact-item:hover .contact-archive-btn {
  display: flex;
}

.contact-archive-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

/* Right Panel: Chat */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-card);
  flex-shrink: 0;
}

.btn-back {
  background: none;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
}

.mobile-only { display: none; }

.chat-header-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(138, 63, 252, 0.15);
  color: #8A3FFC;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.chat-header-avatar .avatar-img { width: 38px; height: 38px; }

.chat-header-info { flex: 1; min-width: 0; }
.chat-header-name { font-size: 14px; font-weight: 700; color: var(--text-primary, #f9fafb); }
.chat-header-sub { font-size: 12px; color: var(--text-secondary, #9ca3af); margin-top: 1px; }

.chat-header-actions { display: flex; align-items: center; gap: 8px; }

.btn-open-fb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  color: var(--text-secondary, #9ca3af);
  text-decoration: none;
  transition: all 0.15s;
}

.btn-open-fb:hover { border-color: #006AFF; color: #006AFF; }

/* Messages */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.messages-loading, .messages-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

.message-bubble-wrap {
  display: flex;
  flex-direction: column;
}

.message-bubble-wrap.outgoing { align-items: flex-end; }
.message-bubble-wrap.incoming { align-items: flex-start; }

/* Reply button on message hover */
.reply-btn {
  opacity: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
  flex-shrink: 0;
  align-self: center;
}

.message-bubble-wrap:hover .reply-btn {
  opacity: 1;
  pointer-events: auto;
}

.reply-btn:hover {
  background: var(--border-primary);
  color: var(--text-primary);
}

/* Outgoing: [reply-btn] [bubble] right-aligned */
.message-bubble-wrap.outgoing {
  flex-direction: row;
  justify-content: flex-end;
  gap: 6px;
}

/* Incoming: [bubble] [reply-btn] left-aligned — reorder via CSS */
.message-bubble-wrap.incoming {
  flex-direction: row;
  justify-content: flex-start;
  gap: 6px;
}

.message-bubble-wrap.incoming .reply-btn { order: 1; }
.message-bubble-wrap.incoming .message-bubble { order: 0; }

/* Reply quote inside bubble */
.msg-reply-quote {
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
}

.outgoing .msg-reply-quote {
  background: rgba(0,0,0,0.18);
  border-left: 3px solid rgba(255,255,255,0.6);
}

.incoming .msg-reply-quote {
  background: rgba(0,0,0,0.06);
  border-left: 3px solid #006AFF;
}

.dark .incoming .msg-reply-quote {
  background: rgba(255,255,255,0.07);
}

.reply-quote-sender {
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.outgoing .reply-quote-sender { color: rgba(255,255,255,0.8); }
.incoming .reply-quote-sender { color: #006AFF; }

.reply-quote-body {
  display: flex;
  align-items: center;
  gap: 6px;
}

.reply-quote-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.reply-quote-text {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.85;
}

/* Reply preview bar above input */
.reply-preview-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-primary);
}

.reply-preview-line {
  width: 3px;
  height: 36px;
  background: #006AFF;
  border-radius: 2px;
  flex-shrink: 0;
}

.reply-preview-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reply-preview-label {
  font-size: 11px;
  font-weight: 700;
  color: #006AFF;
}

.reply-preview-text {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-bubble {
  max-width: 70%;
  padding: 9px 13px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.45;
  position: relative;
}

.outgoing .message-bubble {
  background: linear-gradient(135deg, #8A3FFC, #006AFF);
  color: white;
  border-bottom-right-radius: 4px;
}

.incoming .message-bubble {
  background: #f0f2f5;
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
}

.dark .incoming .message-bubble {
  background: var(--bg-card);
}

.msg-text { display: block; word-break: break-word; }
.msg-time {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-size: 10px;
  margin-top: 4px;
  opacity: 0.7;
  text-align: right;
}

.bubble-sending { opacity: 0.7; }

.msg-sending-indicator {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-style: italic;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.icon-spin { animation: spin 1s linear infinite; }

.msg-image {
  max-width: 220px;
  border-radius: 8px;
  display: block;
  margin-bottom: 4px;
}

.msg-image-clickable {
  cursor: zoom-in;
  transition: opacity 0.15s;
}

.msg-image-clickable:hover { opacity: 0.9; }

.msg-video { max-width: 220px; border-radius: 8px; display: block; margin-bottom: 4px; }
/* ── Conversation Audio Player (Messenger-style) ───────────────────── */
.msg-audio-player {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 220px;
  margin-bottom: 4px;
}

.msg-audio-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s, transform 0.1s;
}
.msg-audio-btn:hover { opacity: 0.85; }
.msg-audio-btn:active { transform: scale(0.92); }

.outgoing .msg-audio-btn {
  background: rgba(255,255,255,0.22);
  color: white;
}
.incoming .msg-audio-btn {
  background: #006AFF;
  color: white;
}

.msg-waveform {
  flex: 1;
  height: 28px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
}
.msg-wavebars {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 2px;
}
.msg-wavebars-fg { transition: clip-path 0.1s linear; }

.msg-wavebar {
  flex: 1;
  border-radius: 1.5px;
  min-height: 3px;
}

.outgoing .msg-wavebars-bg .msg-wavebar { background: rgba(255,255,255,0.3); }
.outgoing .msg-wavebars-fg .msg-wavebar { background: white; }
.incoming .msg-wavebars-bg .msg-wavebar { background: var(--border-secondary); }
.incoming .msg-wavebars-fg .msg-wavebar { background: #006AFF; }

.msg-audio-dur {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  white-space: nowrap;
  min-width: 30px;
  text-align: right;
}
.outgoing .msg-audio-dur { color: rgba(255,255,255,0.75); }
.incoming .msg-audio-dur { color: var(--text-secondary, #9ca3af); }
.msg-doc {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: inherit;
  text-decoration: underline;
  font-size: 12px;
  margin-bottom: 4px;
}

.msg-attachment-fallback {
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
  opacity: 0.8;
}

/* Attachment preview bar */
.attachment-preview-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  background: var(--bg-secondary, #111827);
  border-top: 1px solid var(--border-primary);
  flex-shrink: 0;
}

.preview-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

.doc-preview-bar {
  gap: 8px;
  color: var(--text-secondary, #9ca3af);
}

.preview-label {
  flex: 1;
  font-size: 12px;
  color: var(--text-secondary, #9ca3af);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-compressing {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary, #9ca3af);
  font-style: italic;
}

.btn-remove-preview {
  background: rgba(239, 68, 68, 0.15);
  border: none;
  color: #ef4444;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.btn-remove-preview:hover { background: rgba(239, 68, 68, 0.3); }

/* Input area */
.chat-input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-card);
  flex-shrink: 0;
}

/* Icon action buttons (attach + QR) */
.btn-icon-action {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: none;
  border: 1px solid var(--border-primary);
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  position: relative;
}

.btn-icon-action:hover:not(:disabled) {
  border-color: #006AFF;
  color: #006AFF;
}

.btn-icon-action:disabled { opacity: 0.4; cursor: not-allowed; }

/* Attachment wrapper */
.attachment-wrapper {
  position: relative;
  flex-shrink: 0;
}

.attachment-menu {
  position: absolute;
  bottom: 46px;
  left: 0;
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  overflow: hidden;
  min-width: 140px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  z-index: 50;
}

.attachment-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  color: var(--text-primary, #f9fafb);
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;
}

.attachment-menu-item:hover { background: var(--bg-secondary, #111827); }

/* Quick replies */
.quick-replies-wrapper {
  position: relative;
  flex-shrink: 0;
}

.btn-qr { }

.qr-count {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #8A3FFC, #006AFF);
  color: white;
  font-size: 9px;
  font-weight: 700;
  border-radius: 8px;
  padding: 1px 4px;
  min-width: 14px;
  text-align: center;
  line-height: 1.4;
}

.quick-replies-dropdown {
  position: absolute;
  bottom: 46px;
  left: 0;
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  min-width: 280px;
  max-width: 340px;
  max-height: 320px;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  z-index: 50;
}

.qr-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

.qr-empty {
  padding: 16px;
  text-align: center;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

.qr-empty p { margin: 0 0 8px; }

.qr-create-link {
  color: #006AFF;
  text-decoration: none;
  font-size: 12px;
}

.qr-list { padding: 6px; }

.qr-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
  margin-bottom: 2px;
}

.qr-item:hover { background: var(--bg-secondary, #111827); }

.qr-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

.qr-text-wrap { flex: 1; min-width: 0; }

.qr-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #f9fafb);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-preview {
  font-size: 11px;
  color: var(--text-secondary, #9ca3af);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-img-only { font-style: italic; }

/* Textarea */
.chat-input {
  flex: 1;
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-primary);
  color: var(--text-primary, #f9fafb);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  resize: none;
  outline: none;
  min-height: 38px;
  max-height: 120px;
  font-family: inherit;
  line-height: 1.4;
}

.chat-input:focus { border-color: #006AFF; }

.btn-send {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8A3FFC, #006AFF);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.btn-send:hover:not(:disabled) { opacity: 0.85; }
.btn-send:disabled { opacity: 0.4; cursor: not-allowed; }

/* Empty state (no conversation selected) */
.chat-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--text-secondary, #9ca3af);
}

.empty-logo { opacity: 0.3; }
.chat-empty-state h3 { font-size: 16px; font-weight: 600; margin: 0; color: var(--text-primary, #f9fafb); }
.chat-empty-state p { font-size: 13px; margin: 0; }

/* Image Viewer Lightbox */
.image-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-viewer-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.image-viewer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-end;
}

.btn-viewer-close,
.btn-viewer-download {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background 0.15s;
}

.btn-viewer-close:hover,
.btn-viewer-download:hover {
  background: rgba(255,255,255,0.2);
}

.viewer-img {
  max-width: 88vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  display: block;
}

/* Spinners */
.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-primary);
  border-top-color: #006AFF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Responsive */
@media (max-width: 768px) {
  .main-content { margin-left: 0; }

  .messenger-container { position: relative; }

  .conversations-panel {
    width: 100%;
    max-width: 100%;
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  .conversations-panel.mobile-hidden { display: none; }

  .chat-panel {
    position: absolute;
    inset: 0;
    z-index: 2;
    background: var(--bg-primary, #111827);
  }

  .mobile-only { display: flex; }
  .chat-empty-state { display: none; }
}

/* ── Media Gallery Button ──────────────────────────────────────────── */
.media-gallery-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: none;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.media-gallery-btn:hover {
  background: var(--bg-hover, rgba(255,255,255,0.07));
  color: var(--text-primary, #f9fafb);
}

.media-badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background: #006AFF;
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ── Media Gallery Overlay + Panel ───────────────────────────────── */
.media-gallery-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  justify-content: flex-end;
}

.media-gallery-panel {
  width: 360px;
  max-width: 100vw;
  background: var(--bg-secondary, #1f2937);
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: -4px 0 24px rgba(0,0,0,0.4);
  animation: slideInRight 0.2s ease;
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}

.media-gallery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;
}
.media-gallery-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.close-gallery-btn {
  background: none;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  transition: color 0.15s;
}
.close-gallery-btn:hover { color: var(--text-primary, #f9fafb); }

/* ── Tabs ─────────────────────────────────────────────────────────── */
.media-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;
  overflow-x: auto;
}
.media-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary, #9ca3af);
  font-size: 11px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.media-tab:hover { color: var(--text-primary, #f9fafb); }
.media-tab.active {
  color: #006AFF;
  border-bottom-color: #006AFF;
}

/* ── Loading ──────────────────────────────────────────────────────── */
.media-gallery-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  color: var(--text-secondary, #9ca3af);
  font-size: 14px;
}

/* ── Content ──────────────────────────────────────────────────────── */
.media-gallery-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

/* No media placeholder */
.no-media {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

/* ── Image / Video Grid ───────────────────────────────────────────── */
.media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: var(--bg-tertiary, #111827);
}
.media-item img,
.media-video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.media-item-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.2s;
}
.media-item:hover .media-item-overlay {
  background: rgba(0,0,0,0.5);
}
.media-download-btn,
.media-play-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background 0.15s;
}
.media-item:hover .media-download-btn,
.media-item:hover .media-play-btn {
  opacity: 1;
}
.media-download-btn:hover,
.media-play-btn:hover {
  background: rgba(255,255,255,0.3);
}
.media-item-date {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4px 6px;
  background: linear-gradient(transparent, rgba(0,0,0,0.6));
  font-size: 9px;
  color: rgba(255,255,255,0.85);
  text-align: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.media-item:hover .media-item-date { opacity: 1; }

/* ── Audio + Document List ────────────────────────────────────────── */
.media-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-audio-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-tertiary, #111827);
  border-radius: 12px;
  padding: 12px 14px;
}
.audio-play-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #006AFF;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.1s;
}
.audio-play-btn:hover { background: #0055cc; }
.audio-play-btn:active { transform: scale(0.93); }
.audio-play-btn.playing { background: #10b981; }
.audio-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.audio-waveform {
  height: 4px;
  background: rgba(255,255,255,0.12);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.audio-waveform-fill {
  height: 100%;
  background: #006AFF;
  border-radius: 2px;
  transition: width 0.1s linear;
}
.audio-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: var(--text-secondary, #9ca3af);
}
.audio-time-current,
.audio-time-total {
  font-variant-numeric: tabular-nums;
  min-width: 28px;
}

.media-document-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--bg-tertiary, #111827);
  border-radius: 8px;
  padding: 10px 12px;
}
.document-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: var(--text-secondary, #9ca3af);
}
.document-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.document-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #f9fafb);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}
.document-date {
  font-size: 11px;
  color: var(--text-secondary, #9ca3af);
}
.document-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.document-view-btn,
.media-download-btn-small {
  background: none;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.document-view-btn:hover,
.media-download-btn-small:hover {
  background: var(--bg-hover, rgba(255,255,255,0.07));
  color: var(--text-primary, #f9fafb);
}
</style>
